import { isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  PLATFORM_ID,
  Type,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { DialogComponent } from '../../dialogs/dialog/dialog.component';

@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly dialogStack: {
    componentRef: ComponentRef<DialogComponent>;
    hostElement: HTMLElement;
  }[] = [];

  private readonly envInjector = inject(EnvironmentInjector);
  private readonly appRef = inject(ApplicationRef);
  private readonly platformId = inject(PLATFORM_ID);

  private syncModalDomState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const activeHosts = new Set(this.dialogStack.map((dialog) => dialog.hostElement));
    document.querySelectorAll('popup-component').forEach((popupElement) => {
      if (!activeHosts.has(popupElement as HTMLElement)) {
        popupElement.remove();
      }
    });

    const backdrops = Array.from(document.querySelectorAll('.modal-backdrop'));
    if (this.dialogStack.length === 0) {
      backdrops.forEach((backdrop) => backdrop.remove());
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      return;
    }

    if (backdrops.length > 1) {
      backdrops.slice(0, -1).forEach((backdrop) => backdrop.remove());
    }
    document.body.classList.add('modal-open');
  }

  /**
   * Generates a secure random ID for modal elements
   * Uses crypto.getRandomValues() when available, falls back to a more secure alternative
   */
  public generateSecureModalId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers using timestamp and performance for better entropy
    const timestamp = Date.now().toString(36);
    const perfValue = (performance?.now() || 0).toString(36).replace('.', '');
    return 'modal-' + timestamp + perfValue.slice(0, 8);
  }

  openModal<T, Result = unknown>(
    component: Type<T>,

    config: {
      props?: {
        [key: string]: unknown;
      };
      context?: Record<string, unknown>;
      cssClass?: string;
      modalId?: string;
    } = {}
  ): Observable<Result> {
    const close$ = new Subject<Result>();

    if (!isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        close$.next(undefined as Result);
        close$.complete();
      });
      return close$.asObservable();
    }

    // Create container for the dynamic dialog
    const dialogContainer = document.createElement('popup-component');
    if (config.cssClass) {
      dialogContainer.classList.add(config.cssClass);
    }
    document.body.appendChild(dialogContainer);

    const componentRef = createComponent(DialogComponent, {
      environmentInjector: this.envInjector,
      hostElement: dialogContainer,
    });

    componentRef.setInput('inputContext', config.props ?? null);

    this.appRef.attachView(componentRef.hostView);

    const dialogInstance = componentRef.instance;
    dialogInstance.template = component;
    dialogInstance.context = config.context;
    dialogInstance.cssClass = config.cssClass ?? '';
    dialogInstance.modalId = config.modalId || this.generateSecureModalId();

    dialogInstance.closeEvent.subscribe((result: unknown) => {
      this.closeDialogByRef(componentRef);
      close$.next(result as Result);
      close$.complete();
    });

    this.dialogStack.push({ componentRef, hostElement: dialogContainer });
    setTimeout(() => this.syncModalDomState());

    return close$.asObservable();
  }

  private closeDialogByRef(componentRef: ComponentRef<DialogComponent>): void {
    const dialogIndex = this.dialogStack.findIndex(
      (dialog) => dialog.componentRef === componentRef
    );
    if (dialogIndex === -1) {
      return;
    }

    const [dialogToClose] = this.dialogStack.splice(dialogIndex, 1);
    if (dialogToClose) {
      this.cleanupDialog(dialogToClose);
    } else {
      this.syncModalDomState();
    }
  }

  /**
   * Safely cleans up a dialog component and its DOM element
   * Handles cases where the dialog may have already been removed from DOM
   */
  private cleanupDialog(topDialog: {
    componentRef: ComponentRef<DialogComponent>;
    hostElement: HTMLElement;
  }): void {
    if (!topDialog) {
      return;
    }

    try {
      // Properly dispose of the modal instance to clean up backdrop
      if (
        topDialog.componentRef?.instance &&
        typeof topDialog.componentRef.instance.disposeModal === 'function'
      ) {
        // Add a small delay to ensure Bootstrap has finished any ongoing operations
        setTimeout(() => {
          try {
            if (topDialog.componentRef?.instance?.disposeModal) {
              topDialog.componentRef.instance.disposeModal();
            }
          } catch (error) {
            console.warn('Error disposing modal:', error);
          }
        }, 50);
      }

      // Wait for animation to complete before cleaning up
      setTimeout(() => {
        try {
          // Clean up the component and element
          if (topDialog.componentRef) {
            topDialog.componentRef.destroy();
          }

          // Remove the host element if it's still in the DOM
          if (topDialog.hostElement?.parentNode === document.body) {
            topDialog.hostElement.remove();
          }
        } catch (error) {
          console.warn('Error cleaning up dialog:', error);
        } finally {
          this.syncModalDomState();
        }
      }, 400); // Increased delay to ensure all Bootstrap operations complete
    } catch (error) {
      console.warn('Error in cleanupDialog:', error);
    } finally {
      this.syncModalDomState();
    }
  }

  closeTopDialog(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const topDialog = this.dialogStack.at(-1);
    if (topDialog) {
      this.closeDialogByRef(topDialog.componentRef);
    }
  }

  closeAllDialogs(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    while (this.dialogStack.length > 0) {
      this.closeTopDialog();
    }
  }

  public closeTopDialogWithData(data?: unknown) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.dialogStack.length === 0) {
      return;
    }

    const topDialog = this.dialogStack.at(-1);
    if (topDialog) {
      try {
        // Emit the data to the closeEvent (this triggers the subscription in openModal)
        if (topDialog.componentRef?.instance?.closeEvent) {
          topDialog.componentRef.instance.closeEvent.emit(data);
        }
      } catch (error) {
        console.warn('Error emitting close event:', error);
      }

      this.closeDialogByRef(topDialog.componentRef);
    }
  }

  hasOpenDialogs(): boolean {
    return this.dialogStack.length > 0;
  }

  /**
   * Checks if a modal with the specified modalId is already open
   */
  isModalOpen(modalId: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return this.dialogStack.some((dialog) => dialog.componentRef?.instance?.modalId === modalId);
  }

  public get openDialogCount(): number {
    return this.dialogStack.length;
  }
}
